import { errorFrom } from "@/lib/errors";
import { creditsErrorCode, costChangedCredits } from "@/lib/creditsApi";
import { isSubscriptionRequired } from "@/lib/billingApi";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, Clapperboard, Loader2, X } from "lucide-react";
import { Layout } from "@/components/hud/Layout";
import { Button } from "@/components/primitives/Button";
import { PlanPanel } from "@/components/direct/PlanPanel";
import { SegmentCard } from "@/components/direct/SegmentCard";
import { addFromClip, createMontage } from "@/lib/montageApi";
import {
  analyzeDirectProject,
  deleteDirectReference,
  directReferencePath,
  generateDirectProject,
  getDirectProject,
  isSingleProject,
  retryDirectSegment,
  type DirectProject,
} from "@/lib/directApi";
import { api } from "@/lib/api";

const POLL_MS = 5000;
const EDITABLE = new Set(["DRAFT", "PLANNED", "TOO_MANY_CLIPS", "ANALYSIS_FAILED"]);

// Authenticated thumbnail (references live in private Object Storage).
function RefThumb({ projectId, refId, index, removable, onRemove }: { projectId: string; refId: string; index: number; removable: boolean; onRemove: () => void }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    api.get(directReferencePath(projectId, refId), { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [projectId, refId]);
  return (
    <div data-testid={`project-reference-${index}`} className="relative h-20 w-20 overflow-hidden rounded-xl border border-[#2E7BFF]/35 bg-black/60">
      {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center"><Loader2 className="h-4 w-4 animate-spin text-[#2E7BFF]" /></div>}
      {removable && (
        <button type="button" data-testid={`project-reference-remove-${index}`} aria-label={t("direct.removeReference")} onClick={onRemove} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/75 text-white hover:bg-[#F28B82]">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export default function DirectProjectPage() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<DirectProject | null>(null);
  const [missing, setMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openingMontage, setOpeningMontage] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const lock = useRef(false);

  const load = useCallback(async () => {
    try {
      setProject(await getDirectProject(id));
    } catch (e: any) {
      if (e?.response?.status === 404) setMissing(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const active = project?.status === "ANALYZING" || project?.status === "GENERATING";
  useEffect(() => {
    if (!active) return;
    const h = setInterval(load, POLL_MS);
    return () => clearInterval(h);
  }, [active, load]);

  const run = async (fn: () => Promise<DirectProject>, fallbackKey: string) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    try {
      setProject(await fn());
    } catch (e: any) {
      const code = creditsErrorCode(e);
      if (code === "COST_CHANGED") {
        const c = costChangedCredits(e);
        toast.error(t("credits.costChanged", { old: c?.old ?? 0, n: c?.next ?? 0 }));
      } else if (isSubscriptionRequired(e)) toast.error(t("billing.subscriptionRequired"), { action: { label: t("billing.subscribe"), onClick: () => navigate("/billing") } });
      else if (code === "INSUFFICIENT_CREDITS") toast.error(t("credits.insufficient"), { action: { label: t("billing.buyCredits"), onClick: () => navigate("/billing#packs") } });
      else toast.error(errorFrom(e, fallbackKey));
      await load();
    } finally {
      window.dispatchEvent(new Event("credits:refresh"));
      setBusy(false);
      lock.current = false;
    }
  };

  const openMontage = async () => {
    if (!project || openingMontage) return;
    setOpeningMontage(true);
    try {
      const m = await createMontage(project.title);
      for (const s of [...project.segments].sort((a, b) => a.index - b.index)) {
        if (s.status === "READY" && s.clip_id) await addFromClip(m.id, s.clip_id);
      }
      navigate(`/montage/${m.id}`);
    } catch (e: any) {
      toast.error(errorFrom(e, "direct.montageFailed"));
    } finally {
      setOpeningMontage(false);
    }
  };

  if (missing) {
    return (
      <Layout>
        <main className="mx-auto max-w-3xl px-6 py-24 text-center text-sm text-muted-foreground" data-testid="direct-not-found">{t("direct.notFound")}</main>
      </Layout>
    );
  }
  if (!project) {
    return (
      <Layout>
        <main className="mx-auto flex max-w-3xl items-center gap-2 px-6 py-24 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}</main>
      </Layout>
    );
  }

  const { status, segments, progress, plan } = project;
  const editable = EDITABLE.has(status);
  const generating = status === "GENERATING" || status === "FAILED" || status === "COMPLETED";
  const single = isSingleProject(project);
  const failedSeg = segments.find((s) => s.status === "FAILED");
  const headline =
    status === "COMPLETED" ? t("direct.statusCompleted")
    : status === "FAILED" ? t("direct.statusFailed")
    : status === "GENERATING" ? (single ? t("direct.clipGenerating") : progress.current_index ? t("direct.statusGenerating", { n: progress.current_index, total: progress.total }) : t("direct.statusPrepared"))
    : status === "ANALYZING" ? t("direct.statusAnalyzing")
    : status === "PLANNED" ? t("direct.statusPrepared")
    : "";

  return (
    <Layout>
      <main data-testid="direct-project-page" className="relative mx-auto max-w-5xl px-6 pb-32 pt-14 sm:px-10">
        <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 h-[420px] w-[900px] -translate-x-1/2 rounded-full bg-[#2E7BFF]/[0.10] blur-[150px]" />
        <header className="relative">
          <span className="inline-flex items-center gap-3 font-display text-xs font-semibold uppercase tracking-[0.34em] text-[#7FD4FF]">
            <span className="h-px w-8 bg-gradient-to-r from-[#2E7BFF] to-transparent" />
            {single ? t("direct.eyebrowSingle") : t("direct.eyebrow")} · <span data-testid="direct-aspect">{project.aspect_ratio}</span>
            {single && project.duration_seconds ? <span data-testid="direct-duration"> · {project.duration_seconds} s</span> : null}
          </span>
          <h1 data-testid="direct-title" className="mt-4 font-display text-3xl font-bold uppercase leading-[1] tracking-wide text-foreground sm:text-4xl lg:text-5xl">{project.title}</h1>
          {headline && (
            <p data-testid="direct-status" className={"mt-4 font-display text-sm font-semibold uppercase tracking-[0.2em] " + (status === "FAILED" ? "text-[#FF8A8A]" : status === "COMPLETED" ? "text-[#3DDC84]" : "text-[#7FD4FF]")}>
              {status === "GENERATING" && <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />}{headline}
              {plan && generating && !single && <span className="ml-3 text-muted-foreground">· {plan.mode === "single_clip" ? t("direct.modeSingle") : t("direct.modeMulti", { n: plan.clips.length })}</span>}
            </p>
          )}
        </header>

        <section className="glass-panel relative mt-10 rounded-2xl p-5 sm:p-7">
          <button type="button" data-testid="direct-prompt-toggle" onClick={() => setShowPrompt((v) => !v)} className="flex w-full items-center justify-between text-left text-[11px] font-semibold uppercase tracking-[0.26em] text-[#7FD4FF]">
            {t("direct.prompt")} <ChevronDown className={"h-4 w-4 transition-transform " + (showPrompt ? "rotate-180" : "")} />
          </button>
          <p data-testid="direct-prompt" className={"mt-3 whitespace-pre-wrap text-sm text-muted-foreground " + (showPrompt ? "" : "line-clamp-3")}>{project.prompt}</p>
          {project.references.length > 0 && (
            <div className="mt-5">
              <span className="text-[11px] font-semibold uppercase tracking-[0.26em] text-[#7FD4FF]">{t("direct.references")}</span>
              <div data-testid="direct-references" className="mt-3 flex flex-wrap gap-3">
                {project.references.map((r, i) => (
                  <RefThumb key={r.id} projectId={project.id} refId={r.id} index={i} removable={editable && !busy}
                    onRemove={() => run(() => deleteDirectReference(project.id, r.id), "errors.generic")} />
                ))}
              </div>
            </div>
          )}
        </section>

        {!generating && (
          <section className="glass-panel relative mt-6 rounded-2xl p-5 sm:p-7">
            <PlanPanel project={project} busy={busy}
              onGenerate={() => run(() => generateDirectProject(project.id, project.credits_required ?? null), "direct.generateFailed")}
              onReanalyze={() => run(() => analyzeDirectProject(project.id), "direct.analyzeFailed")} />
          </section>
        )}

        {generating && (
          <section className="relative mt-8 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {!single && <p data-testid="direct-progress" className="text-sm text-muted-foreground">{t("direct.progress", { ready: progress.ready, total: progress.total })}</p>}
              {status === "GENERATING" && <p className="text-xs text-muted-foreground">{t("direct.leaveHint")}</p>}
            </div>
            {status === "FAILED" && failedSeg && !single && (
              <p data-testid="direct-failed-msg" className="text-sm text-[#FF8A8A]">{t("direct.failedAt", { n: failedSeg.index })}</p>
            )}
            <div className="grid gap-4">
              {segments.map((s) => (
                <SegmentCard key={s.id} single={single} segment={s} aspect={project.aspect_ratio} busy={busy} canRetry={status === "FAILED"}
                  onRetry={(index) => run(() => retryDirectSegment(project.id, index), "direct.generateFailed")} />
              ))}
            </div>
            {status === "COMPLETED" && (
              <div className="flex justify-end border-t border-[#2E7BFF]/15 pt-6">
                <Button data-testid="direct-open-montage-btn" variant="yellow" disabled={openingMontage} onClick={openMontage}>
                  {openingMontage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Clapperboard className="h-4 w-4" />}
                  {openingMontage ? t("direct.openingMontage") : t("direct.openMontage")}
                </Button>
              </div>
            )}
          </section>
        )}
      </main>
    </Layout>
  );
}
